import { format } from "date-fns";
import {
  ArchiveIcon,
  ArrowLeftIcon,
  DotIcon,
  DownloadIcon,
  LanguagesIcon,
  PencilIcon,
  UsersIcon,
  XIcon,
} from "lucide-react-native";
import React, { memo, type ReactNode } from "react";
import { GestureResponderEvent, Platform } from "react-native";
import { WebView } from "react-native-webview";

import ArchiveScoutReportDialog from "@/features/reports/components/molecules/ArchiveScoutReportDialog";
import TranslationUnavailableNotice from "@/features/reports/components/molecules/TranslationUnavailableNotice";

import BadgeComponent from "@/components/molecules/Badge";
import FormLoadingSkeleton from "@/components/molecules/FormLoadingSkeleton";
import LanguageSelector from "@/components/molecules/LanguageSelector";
import Markdown from "@/components/molecules/Markdown";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

import type { UiLocale } from "@/constants/locales";
import {
  PlayerDetailMatchDetailFragment,
  SortField,
} from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

import type { TFunction } from "i18next";
import { RADIUS } from "@/config/radius";

/*
 * design-system: dynamic-style waiver — web-only <iframe> CSS fill, the WebView
 * flex fill (not a NativeWind className surface), and measured safe-area insets;
 * passed as single style references so no inline object literal renders on a
 * JSX element.
 */
const IFRAME_STYLE = { width: "100%", height: "100%", border: "none" } as const;
const FLEX_FILL_STYLE = { flex: 1 };
const paddingTopStyle = (paddingTop: number) => ({ paddingTop });

/**
 * The date pattern the match line renders. A `date-fns` FORMAT STRING, not
 * copy: it names fields, and translating it would change what the digits mean.
 * Held at module scope so it is nowhere near the localized JSX. Locale-aware
 * date formatting is explicitly out of scope for this epic (SE-6512).
 */
const MATCH_DATE_FORMAT = "dd/MM/yyyy";

/**
 * Returns the "Matches:" / "Match:" heading in the REPORT's language.
 *
 * SE-6512 deleted the hand-rolled six-locale `MATCHES_HEADING_TRANSLATIONS` map
 * this replaces; the copy now lives in the catalogs under
 * `reports.matchesHeading` with i18next plural keys, so a seventh locale is a
 * catalog entry rather than a code change.
 *
 * The per-call `lng` override is the point. The report body is translated
 * server-side via `translateScoutReport`, so this chrome label must track the
 * PICKER locale — otherwise an English heading sits on top of a Spanish report,
 * which is the mismatch SE-4579 fixed. It is the ONE string in this file that
 * deliberately ignores the app's UI locale; every other call below uses `t`
 * plainly, because the rest of the chrome belongs to the app, not the report.
 *
 * Unknown or absent locales fall back to English through i18next's own
 * `fallbackLng`, and pluralization follows the resolved language's CLDR rules
 * rather than a hand-written `> 1` test.
 *
 * The override rides on the passed-in `t` — react-i18next binds `t` with no
 * fixed language, so an explicit `options.lng` wins — rather than on a
 * module-scope import of the app instance. That matters beyond style:
 * importing `@/i18n` here would pull the whole init chain in at module load,
 * which throws in any suite that mocks `@/constants/locales` (the SE-6505
 * value-vs-type hazard, met again from the other direction).
 * @param t - The catalog translate function
 * @param locale - The currently selected translation locale (null = original)
 * @param matchCount - Number of matches associated with the report
 * @returns The heading, pluralized and in the report's language
 */
const getMatchesHeading = (
  t: TFunction,
  locale: string | null | undefined,
  matchCount: number
): string =>
  t("reports.matchesHeading", {
    count: matchCount,
    ...(locale ? { lng: locale } : {}),
  });

/**
 * Returns the themed foreground class for primary text (icons, headings, body)
 * @param isDark - Whether dark mode is active
 */
const getForegroundClass = (isDark: boolean): string =>
  isDark ? "text-white" : "text-content-primary";

/**
 * Returns the themed class for the modal content background
 * @param isDark - Whether dark mode is active
 */
const getContentBgClass = (isDark: boolean): string =>
  isDark ? "bg-black" : ifWeb("", "bg-surface-muted");

/**
 * Returns the themed class for the modal header border
 * @param isDark - Whether dark mode is active
 */
const getHeaderBorderClass = (isDark: boolean): string =>
  isDark ? "border-b-needs-semantic-outline-600" : "border-b-outline-strong";

/**
 * Returns the themed pill button background class for neutral actions (Share, Edit)
 * @remarks SE-6800b: dark:bg-zinc-800/light:bg-gray-100 collapsed to a single
 * bg-surface-raised token (worst=5, Δlight=[3,2,0], Δdark=[0,-1,-5]).
 */
const PILL_BG_CLASS = "bg-surface-raised";

/**
 * Returns the themed pill button text/icon class for neutral actions
 * @remarks SE-6800b: dark:text-gray-200/light:text-gray-700 collapsed to a
 * single text-content-secondary token (worst=27, Δlight=[27,17,1],
 * Δdark=[-10,-12,-15]).
 */
const PILL_FG_CLASS = "text-content-secondary";

/**
 * Returns the icon color for neutral pill buttons
 * @param isDark - Whether dark mode is active
 */
const getPillIconColor = (isDark: boolean): string =>
  isDark ? "#E5E7EB" : "#374151";

/**
 * Returns themed markdown styles for report content sections
 * Adds visible dividers between Q&A sections and proper spacing
 * @param isDark - Whether dark mode is active
 */
const getReportMarkdownStyle = (isDark: boolean) => {
  // SE-5433: the markdown library ships hardcoded light-gray (#f5f5f5)
  // backgrounds for code / blockquote nodes with no text-color override, so
  // report content that lands in one (e.g. a Summary containing "<80%", which
  // the parser treats as inline code) rendered as near-white text on a
  // near-white box in dark mode. Share a theme-appropriate surface + legible
  // text color across every boxed node.
  const boxed = {
    backgroundColor: isDark ? "#27272A" : "#F4F4F5",
    color: isDark ? "#F4F4F5" : "#18181B",
    borderWidth: 0,
  };
  return {
    hr: {
      backgroundColor: isDark ? "#3F3F46" : "#D4D4D8",
      height: 1,
      marginVertical: 16,
    },
    strong: {
      fontSize: 16,
      fontWeight: "bold" as const,
      color: isDark ? "#FFFFFF" : "#18181B",
    },
    paragraph: {
      marginVertical: 6,
      color: isDark ? "#D4D4D8" : "#3F3F46",
      fontSize: 15,
      lineHeight: 22,
    },
    body: {
      color: isDark ? "#D4D4D8" : "#3F3F46",
      fontSize: 15,
      lineHeight: 22,
    },
    code_inline: { ...boxed, borderRadius: RADIUS.sm, paddingHorizontal: 4 },
    code_block: { ...boxed, borderRadius: RADIUS.md, padding: 12 },
    fence: { ...boxed, borderRadius: RADIUS.md, padding: 12 },
    blockquote: {
      ...boxed,
      borderRadius: RADIUS.md,
      marginVertical: 6,
      padding: 12,
    },
  };
};

/**
 * Picks the catalog key for a match line from the ground value.
 *
 * The home/away marker used to be a bare `"vs"` / `"@"` prefix concatenated in
 * JSX. It is folded into the line's own key instead, because the marker is copy
 * in some languages ("gegen", "contro") and because a translator needs to move
 * it relative to the opponent and the date rather than receive it as a fixed
 * leading token.
 * @param ground - The match ground value (home/away/null)
 * @returns The catalog key for that line
 */
const matchLineKeyFor = (ground?: string | null): string => {
  if (!ground) return "reports.matchLineNeutral";
  return ground.toLowerCase() === "home"
    ? "reports.matchLineHome"
    : "reports.matchLineAway";
};

/**
 * Renders the body area of the report modal based on the current editing state.
 * Handles three cases: active iframe/WebView editor, loading skeleton, and normal report view.
 * @param isEditing - Whether the report is currently being edited
 * @param editUrl - The edit URL for the embedded editor
 * @param isDark - Whether dark mode is active
 * @param reportText - Markdown report content
 * @param matches - Match details associated with this report
 * @param selectedLocale - Currently selected translation locale (null = original English)
 * @param t - The catalog translate function, supplied by the component
 */
const renderReportModalBody = (
  isEditing: boolean | undefined,
  editUrl: string | null | undefined,
  isDark: boolean,
  reportText: string | undefined,
  matches: (PlayerDetailMatchDetailFragment | null)[] | null | undefined,
  selectedLocale: string | null | undefined,
  t: TFunction
): React.JSX.Element => {
  if (isEditing && editUrl) {
    return (
      <Box className="flex-1">
        {Platform.OS === "web" ? (
          <iframe
            src={editUrl}
            title={t("reports.editorTitle")}
            // eslint-disable-next-line no-restricted-syntax -- web-only <iframe> DOM element; CSS fill on a DOM node, not a NativeWind surface
            style={IFRAME_STYLE}
          />
        ) : (
          <WebView
            // eslint-disable-next-line no-restricted-syntax -- react-native-webview RN-only primitive, no atom; flex fill is not a NativeWind surface
            style={FLEX_FILL_STYLE}
            source={{ uri: editUrl }}
            domStorageEnabled
            javaScriptEnabled
            originWhitelist={["https://*"]}
          />
        )}
      </Box>
    );
  }
  if (isEditing) {
    return <FormLoadingSkeleton isDark={isDark} />;
  }
  return (
    <ModalBody contentContainerClassName="px-6 pb-6 text-white">
      {!!matches?.filter(m => !!m?.opponent)?.length && (
        <Stack>
          <Heading variant="title" className={getForegroundClass(isDark)}>
            {getMatchesHeading(
              t,
              selectedLocale,
              matches.filter(m => !!m?.opponent).length
            )}
          </Heading>
          {matches
            .filter(m => !!m?.opponent)
            .map((match, index: number) => (
              <Stack
                direction="horizontal"
                key={`match_${index}`}
                className="pl-2"
              >
                <Icon as={DotIcon} size="xl" />
                <Text variant="unstyled" className={getForegroundClass(isDark)}>
                  {t(matchLineKeyFor(match?.ground), {
                    opponent: match?.opponent ?? "",
                    date: match?.matchDate
                      ? format(new Date(match.matchDate), MATCH_DATE_FORMAT)
                      : "\u2014",
                  })}
                </Text>
              </Stack>
            ))}
        </Stack>
      )}
      <Markdown
        value={reportText ?? ""}
        textColor={isDark ? "#FFFFFF" : "#000000"}
        // eslint-disable-next-line no-restricted-syntax -- markdown-display per-node style-map API, not an RN/element style
        style={getReportMarkdownStyle(isDark)}
      />
    </ModalBody>
  );
};

/**
 * Props for the ReportItemView component
 */
interface Props {
  /**
   * The catalog translate function (SE-6512).
   *
   * Supplied by the container rather than read from `useTranslation()` here:
   * `component-structure/no-return-in-view` requires this view to stay an
   * arrow-shorthand expression, which has nowhere to call a hook. One `t` prop
   * also beats seventeen pre-resolved string props, and because the CONTAINER
   * is the subscriber, a locale change re-renders it and a fresh `t` flows down
   * — which is what satisfies "no restart" (R6) without an effect.
   */
  t: TFunction;
  onPress: (event: GestureResponderEvent) => void;
  teamText?: string;
  dateText?: string;
  scoutName?: string;
  score?: number;
  isModalOpen: boolean;
  reportText?: string;
  onModalClose: () => void;
  topSafeArea: number;
  matches?: (PlayerDetailMatchDetailFragment | null)[] | null;
  isDark: boolean;
  /** Whether the current user is the original author of this report */
  isOwner: boolean;
  canEdit?: boolean | null;
  onEdit?: () => void;
  onShare?: () => void;
  isEditing?: boolean;
  editUrl?: string | null;
  onEditClose?: () => void;
  createdByName?: string;
  createdAtFormatted?: string;
  /** Whether the current user is the last editor of this report */
  isEditor?: boolean;
  editedByName?: string;
  editedAtFormatted?: string;
  canArchive?: boolean | null;
  onArchive?: () => void;
  isArchiveDialogOpen?: boolean;
  onArchiveClose?: () => void;
  onArchiveConfirm?: (confirmation: string) => void;
  isArchiving?: boolean;
  /** Number of users this report has been shared with (VIEWER role) */
  shareCount?: number;
  /** Callback to open the language picker overlay */
  onOpenLanguagePicker?: () => void;
  /** Whether the language picker overlay is visible */
  isLanguagePickerOpen?: boolean;
  /** Callback to close the language picker overlay */
  onCloseLanguagePicker?: () => void;
  /** Callback when a locale is selected from the picker */
  onSelectLocale?: (locale: UiLocale) => void;
  /** Whether a translation is currently in progress */
  isTranslating?: boolean;
  /** Currently selected translation locale (null = original) */
  selectedLocale?: string | null;
  /**
   * Whether the last translation attempt failed. A fact, not a message — the
   * provider's error text never reaches this component (SE-6510 R4a).
   */
  hasTranslationFailure?: boolean;
  /**
   * Whether the body is waiting on a translation that has not arrived yet.
   * True only while a request is genuinely in flight, so the spinner it drives
   * is bounded by the hook's timeout and can never become the permanent state
   * of a report.
   */
  isAwaitingTranslation?: boolean;
  /** Callback to re-attempt the translation that failed */
  onRetryTranslation?: () => void;
  /** Callback to reset to the original language */
  onResetToOriginal?: () => void;
  /** Callback to trigger PDF download of the report (web only) */
  onDownload?: () => void;
  /** Whether PDF generation is currently in progress */
  isDownloading?: boolean;
  /** Optional content rendered inside the modal tree so nested modals stack on native */
  children?: ReactNode;
}

/**
 * Displays a scout report item with summary and full report modal
 * @param root0 - The component props
 * @param root0.onPress - Callback invoked when user taps to open the full report modal
 * @param root0.teamText - Formatted text describing the opposition team(s) for the report
 * @param root0.dateText - Formatted date string showing when the report was created
 * @param root0.scoutName - Name of the scout who authored the report
 * @param root0.score - Overall performance score rating (1-5 scale)
 * @param root0.isModalOpen - Boolean controlling whether the full report modal is displayed
 * @param root0.reportText - Markdown-formatted full report content text
 * @param root0.onModalClose - Callback invoked when user closes the report modal
 * @param root0.topSafeArea - Top safe area inset value for proper modal positioning on notched devices
 * @param root0.matches - Array of match details associated with this report
 * @param root0.isDark - Boolean indicating dark theme is active for conditional styling
 * @param root0.isOwner - Whether the current user is the original author of this report
 * @param root0.canEdit - Whether the current user can edit this report
 * @param root0.onEdit - Callback invoked when user taps the Edit button
 * @param root0.onShare - Callback invoked when user taps the Share button
 * @param root0.isEditing - Whether the form is currently in edit mode
 * @param root0.editUrl - The Form.io edit URL to embed when in edit mode
 * @param root0.onEditClose - Callback to exit edit mode
 * @param root0.createdByName - Name of the scout who originally created the report
 * @param root0.createdAtFormatted - Formatted creation date string
 * @param root0.isEditor - Whether the current user is the last editor
 * @param root0.editedByName - Name of the user who last edited the report
 * @param root0.editedAtFormatted - Formatted last edit date string
 * @param root0.canArchive - Whether the current user can archive this report
 * @param root0.onArchive - Callback invoked when user taps the Archive button
 * @param root0.isArchiveDialogOpen - Whether the archive confirmation dialog is visible
 * @param root0.onArchiveClose - Callback to dismiss the archive dialog
 * @param root0.onArchiveConfirm - Callback when user confirms archival
 * @param root0.isArchiving - Whether the archive mutation is in flight
 * @param root0.shareCount - Number of users this report has been shared with
 * @param root0.onOpenLanguagePicker - Callback to open the language picker
 * @param root0.isLanguagePickerOpen - Whether the language picker is visible
 * @param root0.onCloseLanguagePicker - Callback to close the language picker
 * @param root0.onSelectLocale - Callback when a locale is selected
 * @param root0.isTranslating - Whether a translation is in progress
 * @param root0.selectedLocale - Currently selected translation locale
 * @param root0.hasTranslationFailure - Whether the last translation attempt failed
 * @param root0.isAwaitingTranslation - Whether the body is waiting on a translation
 * @param root0.onRetryTranslation - Callback to re-attempt the failed translation
 * @param root0.onResetToOriginal - Callback to reset to original language
 * @param root0.onDownload - Callback to trigger PDF download of the report
 * @param root0.isDownloading - Whether PDF generation is in progress
 * @param root0.children - Optional content rendered inside the modal tree
 * @param root0.t - Catalog translate function supplied by the container
 */
const ReportItemView = ({
  t,
  onPress,
  teamText,
  dateText,
  scoutName,
  score,
  isModalOpen,
  reportText,
  onModalClose,
  topSafeArea,
  matches,
  isDark,
  isOwner,
  canEdit,
  onEdit,
  onShare,
  isEditing,
  editUrl,
  onEditClose,
  createdByName,
  createdAtFormatted,
  isEditor,
  editedByName,
  editedAtFormatted,
  canArchive,
  onArchive,
  isArchiveDialogOpen,
  onArchiveClose,
  onArchiveConfirm,
  isArchiving,
  shareCount = 0,
  onOpenLanguagePicker,
  isLanguagePickerOpen,
  onCloseLanguagePicker,
  onSelectLocale,
  isTranslating,
  selectedLocale,
  hasTranslationFailure,
  isAwaitingTranslation,
  onRetryTranslation,
  onResetToOriginal,
  onDownload,
  isDownloading,
  children,
}: Props) => (
  <Box className="min-h-20 pt-3">
    <Pressable onPress={onPress}>
      <Stack
        direction="horizontal"
        className="items-center justify-between gap-4 "
      >
        <Stack className="flex-1">
          <Heading variant="title">{teamText || t("reports.untitled")}</Heading>
          <Stack direction="horizontal" className="items-center gap-2">
            <Text
              variant="body-strong"
              // SE-6800b: text-zinc-500 -> text-content-muted (worst=50,
              // Δlight=[27,27,18], Δdark=[50,50,41]).
              className="uppercase text-content-muted"
            >
              {dateText}&nbsp;&middot;&nbsp;
              {isOwner ? t("reports.authorSelf") : scoutName}
            </Text>
            {!isOwner && (
              // SE-6800b: dark:bg-green-900/50/light:bg-green-100 collapsed
              // to bg-status-success-surface (worst=40, Δlight=[17,0,11],
              // Δdark=[8,-40,-12]); dark:text-green-300/light:text-green-700
              // collapsed to text-status-success — role override, the
              // numeric winner text-status-info-strong was family-wrong for
              // a success badge (worst=88, Δlight=[31,3,21], Δdark=[-62,-88,-70]).
              <Box className="rounded-none bg-status-success-surface px-2 py-micro">
                <Text
                  variant="unstyled"
                  className="font-medium text-caption text-status-success"
                >
                  {t("reports.sharedBadge")}
                </Text>
              </Box>
            )}
          </Stack>
        </Stack>
        <Box className="items-end gap-2">
          <BadgeComponent
            type={SortField.Gpr}
            value={score}
            min={1}
            max={5}
            isHigherBetter
          />
        </Box>
      </Stack>
    </Pressable>
    <Modal
      useRNModal={Platform.OS !== "web"}
      isOpen={isModalOpen}
      onClose={onModalClose}
      size="full"
    >
      <ModalBackdrop />
      <ModalContent
        className={`size-full rounded-none border-0 border-none ${getContentBgClass(isDark)} p-0 shadow-none`}
        // eslint-disable-next-line no-restricted-syntax -- measured top safe-area inset; ModalContent is a bare re-export atom (no UNSAFE_style prop), runtime paddingTop off the token ladder
        style={paddingTopStyle(topSafeArea)}
      >
        <ModalHeader
          className={`border-b px-6 py-5 ${getHeaderBorderClass(isDark)}`}
        >
          <Stack className="w-full gap-3">
            <Stack
              direction="horizontal"
              className="w-full items-center justify-between"
            >
              {isEditing ? (
                <Pressable onPress={onEditClose}>
                  <Stack direction="horizontal" className="items-center gap-2">
                    <Icon
                      as={ArrowLeftIcon}
                      size="lg"
                      className={getForegroundClass(isDark)}
                    />
                    <Text
                      variant="body"
                      className={`font-medium ${getForegroundClass(isDark)}`}
                    >
                      {t("reports.back")}
                    </Text>
                  </Stack>
                </Pressable>
              ) : (
                <Pressable onPress={onModalClose}>
                  <Icon
                    as={XIcon}
                    size="lg"
                    className={getForegroundClass(isDark)}
                  />
                </Pressable>
              )}
              {!isEditing && (
                <Stack className="items-end">
                  {!!editedByName && !!editedAtFormatted && (
                    <Text variant="caption" className="text-content-muted">
                      {t("reports.editedBy")}{" "}
                      <Text variant="caption-strong">
                        {isEditor ? t("reports.authorSelf") : editedByName}
                      </Text>{" "}
                      {editedAtFormatted}
                    </Text>
                  )}
                  {!!createdByName && (
                    <Text variant="caption" className="text-content-muted">
                      {t("reports.createdBy")}{" "}
                      <Text variant="caption-strong">
                        {isOwner ? t("reports.authorSelf") : createdByName}
                      </Text>{" "}
                      {createdAtFormatted}
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>
            {!isEditing && (
              // SE-5433: keep Translate / Share / Edit / Archive on a
              // single row. A horizontal ScrollView with a `grow`
              // content container right-aligns the pills when they fit
              // (desktop + wider phones) and scrolls instead of wrapping
              // the Archive button to a second line on narrow screens.
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerClassName="grow items-center justify-end gap-2"
              >
                {!!onOpenLanguagePicker && !selectedLocale && (
                  <Pressable
                    onPress={onOpenLanguagePicker}
                    disabled={!!isTranslating}
                  >
                    <Box
                      className={`flex-row items-center gap-1 rounded-sm px-3 py-2 ${PILL_BG_CLASS}`}
                    >
                      <Icon
                        as={LanguagesIcon}
                        size="xs"
                        color={getPillIconColor(isDark)}
                      />
                      <Text
                        variant="body"
                        className={`font-medium ${PILL_FG_CLASS}`}
                      >
                        {isTranslating
                          ? t("reports.translating")
                          : t("reports.translate")}
                      </Text>
                    </Box>
                  </Pressable>
                )}
                {!!selectedLocale && !!onResetToOriginal && (
                  <Pressable onPress={onResetToOriginal}>
                    <Box
                      className={`flex-row items-center gap-1 rounded-sm px-3 py-2 ${PILL_BG_CLASS}`}
                    >
                      <Icon
                        as={LanguagesIcon}
                        size="xs"
                        color={getPillIconColor(isDark)}
                      />
                      <Text
                        variant="body"
                        className={`font-medium ${PILL_FG_CLASS}`}
                      >
                        {t("reports.showOriginal")}
                      </Text>
                    </Box>
                  </Pressable>
                )}
                {!!onDownload && (
                  <Pressable onPress={onDownload} disabled={!!isDownloading}>
                    <Box
                      className={`flex-row items-center gap-1 rounded-sm px-3 py-2 ${PILL_BG_CLASS}`}
                    >
                      <Icon
                        as={DownloadIcon}
                        size="xs"
                        color={getPillIconColor(isDark)}
                      />
                      <Text
                        variant="body"
                        className={`font-medium ${PILL_FG_CLASS}`}
                      >
                        {isDownloading
                          ? t("reports.downloading")
                          : t("reports.download")}
                      </Text>
                    </Box>
                  </Pressable>
                )}
                {!!canEdit && (
                  <>
                    {!!onShare && (
                      <Pressable onPress={onShare}>
                        <Box
                          className={`flex-row items-center gap-1 rounded-sm px-3 py-2 ${PILL_BG_CLASS}`}
                        >
                          <Icon
                            as={UsersIcon}
                            size="xs"
                            color={getPillIconColor(isDark)}
                          />
                          <Text
                            variant="body"
                            className={`font-medium ${PILL_FG_CLASS}`}
                          >
                            {shareCount > 0
                              ? t("reports.sharedAction")
                              : t("reports.shareAction")}
                          </Text>
                          {shareCount > 0 && (
                            // SE-6800b: bg-amber-500 -> bg-status-warning
                            // (worst=64, Δlight=[-14,-38,29], Δdark=[6,-9,64]).
                            <Box className="size-5 items-center justify-center rounded-full bg-status-warning">
                              <Text
                                variant="caption-strong"
                                className="text-center text-content-on-accent"
                              >
                                {shareCount}
                              </Text>
                            </Box>
                          )}
                        </Box>
                      </Pressable>
                    )}
                    {!!onEdit && (
                      <Pressable onPress={onEdit}>
                        <Box
                          className={`flex-row items-center gap-1 rounded-sm px-3 py-2 ${PILL_BG_CLASS}`}
                        >
                          <Icon
                            as={PencilIcon}
                            size="xs"
                            color={getPillIconColor(isDark)}
                          />
                          <Text
                            variant="body"
                            className={`font-medium ${PILL_FG_CLASS}`}
                          >
                            {t("reports.edit")}
                          </Text>
                        </Box>
                      </Pressable>
                    )}
                    {!!canArchive && !!onArchive && (
                      <Pressable onPress={onArchive}>
                        {/* SE-6800b: bg-red-500 -> bg-status-error (worst=15,
                            Δlight=[-9,-15,-15], Δdark=[0,0,0]). */}
                        <Box className="flex-row items-center gap-1 rounded-sm bg-status-error px-3 py-2">
                          <Icon
                            as={ArchiveIcon}
                            size="xs"
                            className="text-content-on-accent"
                          />
                          <Text
                            variant="body-strong"
                            className="text-content-on-accent"
                          >
                            {t("reports.archive")}
                          </Text>
                        </Box>
                      </Pressable>
                    )}
                  </>
                )}
              </ScrollView>
            )}
            {!isEditing && !!hasTranslationFailure && !!onRetryTranslation && (
              <TranslationUnavailableNotice
                onRetry={onRetryTranslation}
                isRetrying={!!isTranslating}
              />
            )}
          </Stack>
        </ModalHeader>
        {!isEditing && !!isAwaitingTranslation ? (
          <ModalBody contentContainerClassName="px-6 pb-6">
            <Stack
              testID="scout-report-translation-pending"
              className="items-center py-8"
            >
              <Spinner />
            </Stack>
          </ModalBody>
        ) : (
          renderReportModalBody(
            isEditing,
            editUrl,
            isDark,
            reportText,
            matches,
            selectedLocale,
            t
          )
        )}
      </ModalContent>
      {children}
      {!!onArchiveClose && !!onArchiveConfirm && (
        <ArchiveScoutReportDialog
          isOpen={!!isArchiveDialogOpen}
          isArchiving={!!isArchiving}
          onClose={onArchiveClose}
          onConfirm={onArchiveConfirm}
        />
      )}
      {/*
       * The translation picker. The caller owns the trigger (the "Translate"
       * pill in the action row above), so the molecule renders the surface
       * only. `selectedLocale` is passed as STORED — null means the original
       * is on screen and nothing is marked, which is the same rendering an
       * unresolved selection gets and, here, the same truth.
       */}
      {!!onCloseLanguagePicker && !!onSelectLocale && (
        <LanguageSelector
          trigger="none"
          isOpen={!!isLanguagePickerOpen}
          onClose={onCloseLanguagePicker}
          selectedLocale={selectedLocale}
          onSelectLocale={onSelectLocale}
        />
      )}
    </Modal>
  </Box>
);

ReportItemView.displayName = "ReportItemView";

export default memo(ReportItemView);
